import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import NodeList from './NodeList';
import './nodeConfig.less';
import { Tooltip } from 'antd';

interface NodeConfigurationStatusProps {
  nodeCount: number;
  nodeType: String;
  data: any,
  filters?: any;
  configResult: any,
}

const nodeTypes = {
  'control_node': 'Control Nodes',
  'field_node': 'Field Nodes',
  'energy_node': 'Energy Nodes',
};

const tableKeysToRender = ['deviceId', 'deviceName', 'updatedAt', 'configSyncStatus'];

function getListLength(list, key, status) {
  if (Array.isArray(status)) {
    return list.filter(item => status.includes(item[key])).length;
  } else {
    return list.filter(item => item[key] === status).length;
  }
}

function getStatusCount(nodeList: any[], key: any, failedNodeIds: string[] = []) {
  const filteredList = nodeList.filter(
    (node) => !failedNodeIds.includes(node.deviceIdentifier)
  );

  return {
    completed: getListLength(filteredList, key, ['success', 'sync']),
    inProgress: getListLength(filteredList, key, 'pending'),
    error: getListLength(filteredList, key, ['aborted', 'rejected', 'error']),
  };
}

const NodeConfigurationStatus: React.FC<NodeConfigurationStatusProps> = (props: any) => {
  const {
    nodeCount, nodeType, data, filters, configResult
  } = props;
  const { t } = useTranslation();
  function renderStatusDetails(nodeList: any[]) {
    const failedNodeIds = configResult?.failed_nodes || [];
    const statusCount = getStatusCount(nodeList, 'configSyncStatus', failedNodeIds);
    return (
      <>
      <div className="flex-space-between">
        <div>
          <span className="font-weight-bold">{`${t('Completed')}: `}</span>
          <span>{`${statusCount.completed}/${nodeList.length}`}</span>
        </div>
        <div>
          <span className="font-weight-bold">{`${t('Pending')}: `}</span>
          <span>{`${statusCount.inProgress}/${nodeList.length}`}</span>
        </div>
      
        <div>
          <span className="font-weight-bold">{`${t('Failed')}: `}</span>
          <Tooltip
            title={
              configResult?.failed_nodes?.length
                ? configResult.failed_nodes.map((id) => {
                    const match = data?.find((node) => node.deviceIdentifier === id);
                    return (
                      <div key={id}>
                        {match?.deviceName || match?.deviceIdentifier}
                      </div>
                    );
                  })
                : t('No Failed nodes')
              } 
          >
            <span className='failed-node-tooltip'>
              {`${configResult?.failed_nodes?.length}/${nodeList.length}`}
            </span>
          </Tooltip>
        </div>
      </div>
      </>
    );
  }

  return(
    <div>
      <div className="flex-row gap-10 justify-content-space-between">
        <div className='block-div'>
          <label className="font-weight-bold">{`${t('Device Type')}: `}</label>
          <span>{nodeTypes?.[nodeType]}</span>
        </div>

        <div className='block-div'>
          <div className='flex-row gap-10'>
            <label className="font-weight-bold text-nowrap">
              {t('Total number of devices')}
            </label>
            <Input
              value={nodeCount} 
              disabled 
              className='node-config-input font-weight-bold text-center'
            />
          </div>
        </div>
      </div>
      <NodeList
        renderItems={tableKeysToRender}
        showSearch={false}
        isCheckable={false}
        data={data?.filter(
          (node: any) => !configResult?.failed_nodes?.includes(node.deviceIdentifier)
        )}
        displayRecords={5}
        autoRefresh={15}
        filters={filters || { type: nodeType }}
        renderStatusColored={false}
      />
      {renderStatusDetails(data)}
    </div>
  );
};

export default NodeConfigurationStatus;
